'use client';

import { useEntitlements } from '@components/billing/EntitlementsProvider';
import PageContainer from '@components/layout/page-container';
import { PLANS } from '@billing/plans';
import { freePlanRouteAccess } from '@billing/free-plan-routes';
import { useTranslations } from 'next-intl';
import { usePathname } from 'next/navigation';
import { ReactNode } from 'react';
import { FEATURE_ICONS, upgradeHighlightFeatures } from './feature-icons';
import UpgradeState from './UpgradeState';

/** The upgrade card every Free-plan page carries. */
function FreePlanUpgradeCard() {
  const t = useTranslations('billing');
  const { organizationId, features } = useEntitlements();
  const target = PLANS.splash;
  return (
    <UpgradeState
      organizationId={organizationId}
      title={t.rich('free.title', {
        highlight: chunks => <span className="text-brand-text">{chunks}</span>
      })}
      description={t('free.description')}
      highlights={upgradeHighlightFeatures(features, target.id).map(id => ({
        icon: FEATURE_ICONS[id],
        label: t(`features.${id}`)
      }))}
      cta={t('gate.upgrade-now')}
      footnote={t('gate.included-in', { plan: target.name })}
    />
  );
}

/**
 * Free organizations get the classic cocktail library and nothing else: the
 * upgrade card replaces every other page and follows the library pages.
 * Shapes the UI only; plan features are still enforced on the server.
 */
export function FreePlanGate({
  organization_id,
  children
}: {
  organization_id: string;
  children: ReactNode;
}) {
  const pathname = usePathname();
  const access = freePlanRouteAccess(pathname, organization_id);
  if (access === 'open') {
    return <>{children}</>;
  }
  const card = (
    <PageContainer width="narrow">
      <FreePlanUpgradeCard />
    </PageContainer>
  );
  if (access === 'locked') {
    return card;
  }
  return (
    <>
      {children}
      <div className="mt-10">{card}</div>
    </>
  );
}
